import { ArrowRight, Flame } from "lucide-react";
import { SMOKE_IMG } from "@/lib/assets";
import { useOrder } from "@/context/CartContext";

// Fortnightly drop anchor: Saturday 17 October 2026, then +14 days.
function getNextDropDate() {
  const anchor = new Date(2026, 9, 17);
  anchor.setHours(23, 59, 59, 999);
  const now = new Date();
  let drop = new Date(anchor);
  while (drop.getTime() < now.getTime()) {
    drop = new Date(drop.getTime() + 14 * 24 * 60 * 60 * 1000);
  }
  return drop;
}

function formatDropDate(d) {
  const day = d.getDate();
  const rem10 = day % 10;
  const rem100 = day % 100;
  const suffix =
    rem100 >= 11 && rem100 <= 13 ? "th" :
    rem10 === 1 ? "st" :
    rem10 === 2 ? "nd" :
    rem10 === 3 ? "rd" : "th";
  const weekday = d.toLocaleDateString("en-AU", { weekday: "long" });
  const month = d.toLocaleDateString("en-AU", { month: "long" });
  return `${weekday} ${day}${suffix} ${month}`;
}

export default function Hero() {
  const { openWith, inventory } = useOrder();
  const soldOut = inventory.all_sold_out;

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative w-full overflow-hidden pt-28 sm:pt-32 pb-20 sm:pb-24"
    >
      {/* Background layers */}
      <div className="absolute inset-0 bg-[#0A0D26]" />
      <div className="absolute inset-0 smoke-overlay" />
      <img
        src={SMOKE_IMG}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-screen animate-smoke"
      />
      <div className="absolute inset-0 hero-grain" />
      <div className="absolute -bottom-1 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-[#0A0D26]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#F4A623]/40 bg-[#F4A623]/10 text-[#F4A623] text-[10px] uppercase tracking-[0.25em]">
          <Flame size={11} /> Fortnightly Drops · Burn City
        </div>

        <h1
          data-testid="hero-headline"
          className="font-display mt-6 text-[64px] leading-[0.9] sm:text-[88px] md:text-[112px] lg:text-[160px] tracking-tight uppercase text-[#F6F0E8]"
        >
          <span className="block">Best in the</span>
          <span className="block text-[#ED0B09] text-glow-red">South East</span>
        </h1>

        <p
          data-testid="hero-subheadline"
          className="mt-6 mx-auto max-w-xl text-base sm:text-lg text-[#F6F0E8]/80 leading-relaxed"
        >
          Taste of NZ nibbles here in Burn City
        </p>

        <div
          data-testid="hero-drop-date"
          className="mt-4 text-xs sm:text-sm uppercase tracking-[0.25em] text-[#F4A623]"
        >
          Pre-orders for {formatDropDate(getNextDropDate())}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          <button
            type="button"
            onClick={() => openWith(null)}
            data-testid="hero-order-button"
            className={`group inline-flex items-center justify-center gap-2 font-bold uppercase tracking-[0.18em] px-7 py-4 rounded-full transition-all duration-300 ${
              soldOut
                ? "bg-[#171D4F] text-[#F6F0E8] border border-[#F6F0E8]/15"
                : "bg-[#ED0B09] hover:bg-[#B42A27] text-[#F6F0E8] shadow-[0_0_30px_rgba(237,11,9,0.55)] hover:shadow-[0_0_45px_rgba(237,11,9,0.85)] hover:-translate-y-0.5 animate-pulse-glow"
            }`}
          >
            {soldOut ? "Drop Sold Out" : "Pre-order Now"}
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </button>
          <a
            href="#menu"
            data-testid="hero-view-menu-button"
            className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-[#F6F0E8]/80 text-[#F6F0E8] hover:bg-[#F6F0E8] hover:text-[#171D4F] font-bold uppercase tracking-[0.18em] px-7 py-4 rounded-full transition-all duration-300"
          >
            View Menu
          </a>
        </div>
      </div>

      {/* Marquee strip */}
      <div className="relative mt-14 sm:mt-16 border-y border-[#F6F0E8]/10 bg-[#0A0D26]/60 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee py-4 font-display text-2xl sm:text-3xl text-[#F6F0E8]/90 uppercase tracking-wider">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-8 pr-8">
              {[
                "Crispy Outside",
                "Juicy Inside",
                "Burn City Born",
                "Fortnightly Drops",
                "One Flavour. All Killer.",
                "South East Heat",
              ].map((t, i) => (
                <span key={`${k}-${i}`} className="flex items-center gap-8">
                  <span>{t}</span>
                  <span className="text-[#ED0B09]">●</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
